import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormDialog } from '@/components/ui/form-dialog';
import { Printer, X } from 'lucide-react';
import api from '@/lib/api';

/**
 * How much of the order book to put on the payment sheet.
 *
 * The list's own filters already say which orders are in question, but a
 * collection round is cut differently: accounts works last month's bookings, or
 * the newest fifty, not "every active order". So the sheet takes a date window
 * over when the work was booked and a cap on how many rows come out, and says
 * how many orders that actually is before anybody walks to the printer.
 */

// A round is worked off paper. These are the sizes a person actually carries;
// 500 is the server's own ceiling, so All means "as many as it will give".
const SIZES = [25, 50, 100, 200, 500];

const isCompleteDate = (value) => value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value);

export default function PrintListDialog({
  open, onOpenChange, filters, filterLabel, onPrint, printing,
}) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [limit, setLimit] = useState(500);
  const [matching, setMatching] = useState(null);
  const [counting, setCounting] = useState(false);

  // a fresh sheet each time it is opened — last week's window is not this
  // week's question
  useEffect(() => {
    if (open) {
      setDateFrom('');
      setDateTo('');
      setLimit(500);
      setMatching(null);
    }
  }, [open]);

  const selection = useCallback(() => {
    const params = { ...filters };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  }, [filters, dateFrom, dateTo]);

  // How many orders the window holds, asked of the same endpoint the list uses
  // so the number on the button is the number on the sheet.
  useEffect(() => {
    if (!open || !isCompleteDate(dateFrom) || !isCompleteDate(dateTo)) return undefined;
    let live = true;
    setCounting(true);
    api.get('/orders', { params: { ...selection(), page: 1, limit: 1 } })
      .then((res) => { if (live) setMatching(res.data.total || 0); })
      .catch(() => { if (live) setMatching(null); })
      .finally(() => { if (live) setCounting(false); });
    return () => { live = false; };
  }, [open, dateFrom, dateTo, selection]);

  const onSheet = matching == null ? null : Math.min(matching, limit);
  const trimmed = matching != null && matching > limit;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Print order payment list"
      description="Company, items and amounts, with the payment status column left blank to fill in."
      onSubmit={() => onPrint({ ...selection(), limit })}
      className="max-w-lg"
      data-testid="print-list-dialog"
      footer={(
        <>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            <X className="mr-1 h-4 w-4" /> Cancel
          </Button>
          <Button
            type="submit"
            disabled={printing || onSheet === 0}
            className="bg-brand-blue text-white hover:bg-brand-blue-dark"
            data-testid="confirm-print-list-btn"
          >
            <Printer className="mr-1 h-4 w-4" />
            {printing ? 'Preparing…' : 'Print'}
          </Button>
        </>
      )}
    >
      <div className="space-y-4 py-2">
        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          On screen now: <span className="font-medium text-slate-900">{filterLabel}</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600" htmlFor="print-date-from">
              Booked from
            </Label>
            <Input
              id="print-date-from"
              type="date"
              className="h-9"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="print-date-from"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600" htmlFor="print-date-to">
              Booked up to
            </Label>
            <Input
              id="print-date-to"
              type="date"
              className="h-9"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="print-date-to"
            />
          </div>
        </div>
        <p className="-mt-2 text-[11px] text-slate-400">
          Leave both blank for every date the filter covers. The end date is included in full.
        </p>

        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-slate-600">How many orders</Label>
          <div className="flex flex-wrap gap-1.5">
            {SIZES.map((size) => (
              <button
                key={size}
                type="button"
                onClick={() => setLimit(size)}
                aria-pressed={limit === size}
                data-testid={`print-limit-${size}`}
                className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                  limit === size
                    ? 'bg-brand-blue/10 text-brand-blue ring-1 ring-inset ring-brand-blue/30'
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                {size === 500 ? 'All (max 500)' : size}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-400">
            The newest orders first, so a smaller sheet is the most recent work.
          </p>
        </div>

        <p className="text-xs text-slate-600" data-testid="print-list-count">
          {counting && 'Counting…'}
          {!counting && matching == null && 'Could not count the orders in this window.'}
          {!counting && matching === 0 && 'No orders match — nothing to print.'}
          {!counting && matching > 0 && (
            <>
              <span className="font-medium text-slate-900">{onSheet.toLocaleString()}</span>
              {onSheet === 1 ? ' order' : ' orders'} on the sheet
              {trimmed && (
                <span className="text-slate-400">
                  {' '}— {matching.toLocaleString()} match, newest {limit.toLocaleString()} printed
                </span>
              )}
            </>
          )}
        </p>
      </div>
    </FormDialog>
  );
}
